import React, { useEffect, useRef, useState } from "react";
import { useConfig } from "../../../../hooks/useConfig";
import { useRemote } from "../../../../hooks/useRemote";
import { useStreamTime } from "../../../../hooks/useStreamTime";
import { useTV } from "../../../../hooks/useTV";
import useGlobalStore from "../../../../store/global.store";
import VipOffStreamView from "../VipOffStreamView";
import VipSubCheck from "../VipSubCheck";
import "./VipHotel.styles.css";

function VipHotel() {
  const { isTv } = useTV();
  const {
    loading,
    showVipStream,
    showVipStreamLoader,
    getMagicKingdomShowTimeText,
  } = useStreamTime();
  const { config } = useConfig();

  const vipStreamSrc =
    config?.VIPstreamUrl === 1
      ? isTv
        ? "https://encoder3.mywaitserver.com/live/player/720p/"
        : "https://encoder3.mywaitserver.com/live/player/480p/"
      : config?.VIPstreamUrl === 2
      ? "https://encoder2.mywaitserver.com/live/player/"
      : config?.VIPstreamUrl === 3
      ? isTv
        ? "https://encoder.mywaitserver.com/live/player/720p/"
        : "https://encoder.mywaitserver.com/live/player/480p/"
      : config?.VIPstreamUrl === 4
      ? "https://encoder4.mywaitserver.com/live/player/"
      : isTv
      ? "https://encoder3.mywaitserver.com/live/player/720p/"
      : "https://encoder3.mywaitserver.com/live/player/480p/";
  const { updateView } = useRemote();

  const device = useGlobalStore((state) => state.device);
  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  // const setShowVipSelector = useGlobalStore(
  //   (state) => state.setShowVipSelector
  // );
  // const setSelectedVipDoor = useGlobalStore(
  //   (state) => state.setSelectedVipDoor
  // );
  const showVipSelector = useGlobalStore((state) => state.showVipSelector);

  const [infoOn, setInfoOn] = useState(false);
  const [focusedButton, setFocusedButton] = useState("info");
  const [showCoverImage, setShowCoverImage] = useState(false);

  const [showBlackOverlay, setShowBlackOverlay] = useState(false);

  useEffect(() => {
    const maybeShowOverlay = () => {
      if (showVipStream === true) {
        setShowBlackOverlay(true);
        setShowCoverImage(true); // Show cover.jpg immediately

        const coverTimer = setTimeout(() => {
          setShowCoverImage(false); // Hide cover after 5 seconds
        }, 5000);

        const overlayTimer = setTimeout(() => {
          setShowBlackOverlay(false); // Hide black overlay after 8 seconds
        }, 4000);

        return () => {
          clearTimeout(coverTimer);
          clearTimeout(overlayTimer);
        };
      }
    };

    return maybeShowOverlay();
  }, [showVipStream]);

  // Iframe positioning values - using constants instead of state
  const iframeStyle = config?.iframeStyles?.hotel || {
    display: {
      top: "-20%",
      left: "-10%",
      width: "100%",
      height: "100%",
      zoom: 1.1,
      origin: "center center",
    },
    tv: {
      top: "-20%",
      left: "0%",
      width: "100%",
      height: "100%",
      zoom: 1.1,
      origin: "center center",
    },
  };

  const infoButtonRef = useRef(null);
  const exitButtonRef = useRef(null);

  const toggleInfo = () => {
    if (showVipSelector) return;
    setInfoOn((prev) => !prev);
  };

  const handleExit = () => {
    setShowBottomBar(false);
    updateView("VIP_door_2_selector", () => {});
  };

  // Set bottom bar to false on mount
  useEffect(() => {
    setShowBottomBar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-focus info button when component mounts (for TV mode)
  useEffect(() => {
    if (isTv && infoButtonRef.current && !showVipSelector) {
      infoButtonRef.current.focus();
      setFocusedButton("info");
    }
  }, [isTv, showVipSelector]);

  // Keyboard navigation for TV mode
  useEffect(() => {
    if (isTv && !showVipSelector) {
      const handleKeyDown = (e) => {
        const buttons = ["info", "exit"];
        const currentIndex = buttons.indexOf(focusedButton);

        if (e.key === "ArrowDown") {
          e.preventDefault();
          // Navigate to next button or wrap to first
          const nextIndex = (currentIndex + 1) % buttons.length;
          const nextButton = buttons[nextIndex];
          setFocusedButton(nextButton);

          // Focus the appropriate button
          if (nextButton === "info" && infoButtonRef.current) {
            infoButtonRef.current.focus();
          } else if (nextButton === "exit" && exitButtonRef.current) {
            exitButtonRef.current.focus();
          }
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          // Navigate to previous button or wrap to last
          const prevIndex =
            (currentIndex - 1 + buttons.length) % buttons.length;
          const prevButton = buttons[prevIndex];
          setFocusedButton(prevButton);

          // Focus the appropriate button
          if (prevButton === "info" && infoButtonRef.current) {
            infoButtonRef.current.focus();
          } else if (prevButton === "exit" && exitButtonRef.current) {
            exitButtonRef.current.focus();
          }
        } else if (e.key === "Enter") {
          e.preventDefault();

          // Handle Enter key for the focused button
          if (focusedButton === "info") {
            toggleInfo();
          } else if (focusedButton === "exit") {
            handleExit();
          }
        }
      };

      // Add event listener
      window.addEventListener("keydown", handleKeyDown);

      // Clean up
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTv, focusedButton, showVipSelector]);

  // Helper function to apply focus styling
  const getFocusStyle = (buttonId) => {
    if (isTv && focusedButton === buttonId && !showVipSelector) {
      return {
        outline: "none",
        boxShadow: "0 0 0 4px #3ADF3C",
      };
    }
    return {};
  };

  // Get current iframe style based on device type
  const getCurrentIframeStyle = () => {
    const currentSettings = isTv ? iframeStyle.tv : iframeStyle.display;
    return {
      position: "absolute",
      top: currentSettings.top,
      left: currentSettings.left,
      width: currentSettings.width,
      height: currentSettings.height,
      transform: `scale(${currentSettings.zoom})`,
      transformOrigin: currentSettings.origin,
      border: "none",
      zIndex: 0,
    };
  };

  return (
    <div className="vip_hotel-app-container">
      {loading ? (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontSize: "30px",
            color: "blue",
            fontFamily: "Lato",
            zIndex: 50,
          }}
        >
          <span>Loading...</span>
        </div>
      ) : (
        <>
          {/* Blue info bar */}
          {infoOn && getMagicKingdomShowTimeText() && (
            <div
              className="vip_hotel-info-bar"
              style={{
                fontSize: isTv ? "40px" : "30px",
                height: isTv ? "120px" : undefined,
                lineHeight: isTv ? "110px" : undefined,
                fontFamily: "lato",
              }}
            >
              Magic Kingdom Fireworks: {getMagicKingdomShowTimeText()}
            </div>
          )}
          {showBlackOverlay && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "black",
                zIndex: 998,
                opacity: 1,
              }}
            />
          )}
          {showCoverImage && (
            <img
              src={
                isTv
                  ? "/static/vip/transition/tv/hotel/covertv.jpg"
                  : "/static/vip/transition/display/hotel/cover.jpg"
              }
              alt="Cover"
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                objectFit: "cover",
                zIndex: 999,
              }}
            />
          )}

          {showVipStream === true ? (
            <>
              {/* Stream loader animation - Left Door */}
              <div
                style={{
                  position: "absolute",
                  left: showVipStreamLoader ? 0 : "-100vw",
                  transition: showVipStreamLoader ? "none" : "left 9s",
                  top: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{
                    width: "100vw",
                    height: "100vh",
                    objectFit: "cover",
                  }}
                  src={
                    isTv
                      ? "/static/vip/transition/tv/hotel/door-left.png"
                      : "/static/vip/transition/display/hotel/door-left.png"
                  }
                  alt=""
                />
              </div>

              {/* Stream loader animation - Right Door */}
              <div
                style={{
                  position: "absolute",
                  right: showVipStreamLoader ? 0 : "-100vw",
                  transition: showVipStreamLoader ? "none" : "right 9s",
                  top: 0,
                  width: "100vw",
                  height: "100vh",
                  zIndex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <img
                  style={{
                    width: "100vw",
                    height: "100vh",
                    objectFit: "cover",
                  }}
                  src={
                    isTv
                      ? "/static/vip/transition/tv/hotel/door-right.png"
                      : "/static/vip/transition/display/hotel/door-right.png"
                  }
                  alt=""
                />
              </div>

              <iframe
                src={vipStreamSrc}
                title="Live Stream Background"
                style={getCurrentIframeStyle()}
                allow="autoplay"
                allowFullScreen
              />
              <img
                src="/static/stream/livelogo.png"
                alt="Live"
                style={{
                  position: "absolute",
                  top: isTv ? "-10px" : "0px",
                  right: isTv ? "20px" : "10px",
                  width: isTv ? "580px" : "340px",
                  zIndex: 20,
                }}
              />
              <img
                src={
                  isTv
                    ? "/static/vip/tv-hotel.png"
                    : "/static/vip/display-hotel.png"
                }
                alt="TV Hotel"
                className="vip_hotel-fw vip_hotel-fw4"
              />
            </>
          ) : (
            showVipStream === false && (
              <VipOffStreamView
                bgImage={
                  isTv
                    ? "/static/vip/transition/tv/hotel/off.jpg"
                    : "/static/vip/transition/display/hotel/off.jpg"
                }
              />
            )
          )}

          {/* Skewed Last Name */}
          <span
            style={
              isTv
                ? {
                    fontFamily: "Lato",
                    fontSize: "55px",
                    fontWeight: 800,
                    zIndex: 9,
                    position: "absolute",
                    top: "31%",
                    right: "3%",
                    color: "#152338",
                    transform:
                      "perspective(400px) rotateY(-40deg) rotateX(8deg) skew(15deg, -12deg) scale(1)",
                    transformOrigin: "right center",
                    lineHeight: "1.2",
                    whiteSpace: "nowrap",
                    maxWidth: "28vw",
                    textOverflow: "clip",
                    overflow: "visible",
                  }
                : {
                    fontFamily: "Lato",
                    fontSize: "33px",
                    fontWeight: 800,
                    zIndex: 9,
                    position: "absolute",
                    top: "34%",
                    right: "3%",
                    color: "#152338",
                    transform:
                      "perspective(400px) rotateY(-40deg) rotateX(8deg) skew(15deg, -12deg) scale(1)",
                    transformOrigin: "right center",
                    lineHeight: "1.2",
                    whiteSpace: "nowrap",
                    maxWidth: "12vw",
                    textOverflow: "clip",
                    overflow: "visible",
                  }
            }
          >
            {`${device.subscription.last_name} Family`}
          </span>

          {/* INFO */}
{/* CONTROLS */}
{isTv ? (
  <>
    {/* TV — use original CSS classes & sizes/positions */}
    <button
      ref={infoButtonRef}
      className={`vip_hotel-info-button ${infoOn ? "on" : "off"}`}
      onClick={toggleInfo}
      onFocus={() => !showVipSelector && setFocusedButton("info")}
      onBlur={() => (isTv ? null : setFocusedButton(null))}
      style={getFocusStyle("info")}           // green ring when focused
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Toggle Info"
    >
      <span>i</span>
    </button>


    <button
      ref={exitButtonRef}
      className="vip_hotel-exit-button"
      onClick={handleExit}
      onFocus={() => !showVipSelector && setFocusedButton("exit")}
      onBlur={() => (isTv ? null : setFocusedButton(null))}
      style={getFocusStyle("exit")}           // green ring when focused
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Exit"
    >
      EXIT
    </button>
  </>
) : (
  <>
    {/* DISPLAY — simple, bounce-free inline divs */}
    <div
      ref={infoButtonRef}
      role="button"
      onClick={toggleInfo}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleInfo(); } }}
      onFocus={() => !showVipSelector && setFocusedButton("info")}
      onBlur={() => setFocusedButton(null)}
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Toggle Info"
      style={{
        zIndex: 4,
        position: "absolute", left: "30px", bottom: "155px",
        width: 75, height: 75, borderRadius: "50%", backgroundColor: "white",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
        outline: "none", transition: "none", fontFamily: "Lato",
      }}
    >
      <span style={{ fontSize: 32, fontWeight: "bold", fontStyle: "italic", color: "#022758", lineHeight: 1 }}>i</span>
    </div>

    

    <div
      ref={exitButtonRef}
      role="button"
      onClick={handleExit}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleExit(); } }}
      onFocus={() => !showVipSelector && setFocusedButton("exit")}
      onBlur={() => setFocusedButton(null)}
      tabIndex={showVipSelector ? -1 : 0}
      aria-label="Exit"
      style={{
        zIndex: 4,
        position: "absolute", left: "30px", bottom: "55px",
        width: 80, height: 80, borderRadius: "50%", backgroundColor: "red",
        display: "flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", boxShadow: "0 4px 10px rgba(0,0,0,0.4)",
        outline: "none", transition: "none", fontFamily: "Lato",
      }}
    >
      <span style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>EXIT</span>
    </div>
 
</>
)}


</>
)}

      <VipSubCheck />
    </div>
  );
}

export default VipHotel;
